import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

/**
 * L'ÉTAT VIDE UNIQUE (LOT-H10).
 *
 * ══════════════════════════════════════════════════════════════════════════
 * POURQUOI CE FICHIER EXISTE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * La recette DA a trouvé, sur `/metrics`, **deux traitements de l'état vide
 * côte à côte, dans la même rangée** (DA-033) : la carte « Statut des
 * contacts » était un rectangle de ~380 px **entièrement vide** — ni
 * graphique, ni message, ni squelette — pendant que « Meilleurs agents »,
 * à 12 px de là, affichait « Aucune donnée ».
 *
 * Un rectangle vide ne dit pas « il n'y a rien » : il dit « quelque chose est
 * cassé ». Et « Aucune donnée » ne dit rien non plus — c'est un constat sans
 * suite, dans une langue de base de données.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * LA RÉFÉRENCE EST DANS LE PRODUIT
 * ══════════════════════════════════════════════════════════════════════════
 *
 * La recette cite explicitement le panneau vide de `/admin/permissions` comme
 * « l'**un des états vides les mieux traités du produit** — à citer en
 * référence pour H10 » (DA-046). Ce composant en tire les trois éléments :
 *
 *   ① un **glyphe** — grand, neutre, sans couleur de statut : rien n'a
 *      échoué, il n'y a simplement rien encore ;
 *   ② une **phrase** — ce qui manque, en français, pas « Aucune donnée » ;
 *   ③ une **action qui débloque** — celle qui fait sortir de l'état vide.
 *
 * Le troisième est le seul qui coûte quelque chose à écrire, et c'est le seul
 * qui compte. Un état vide sans action est une impasse polie : sur
 * `/sms/templates` et `/emails/templates`, un unique modèle flottait dans un
 * grand vide sans la moindre invitation à en créer un (DA-047).
 *
 * ══════════════════════════════════════════════════════════════════════════
 * LE GLYPHE EST NEUTRE, TOUJOURS
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Pas de tuile teintée, pas d'icône `warning`, pas de rouge. « Vide » n'est
 * ni un succès, ni un avertissement, ni une erreur (§1.2 : « si une valeur
 * n'a pas de sens métier, elle est neutre »). Une liste vide au premier jour
 * d'un compte est l'état NORMAL du produit. Un état vide qui alarme est le
 * même défaut que DA-001, à l'autre bout de l'échelle.
 *
 * Quand quelque chose a réellement échoué, ce n'est pas ce composant : c'est
 * [`ErrorState`](ErrorState.tsx).
 */

/**
 * Densité du bloc.
 *
 * `panel` (défaut) : l'état vide occupe une zone de contenu — une liste, une
 * page, un panneau de détail. Il respire.
 *
 * `compact` : l'état vide tient dans une **carte de graphique**. Les cartes de
 * `/metrics` bornent leur contenu à 180 px de haut : le padding de `panel`
 * (2 × 48 px) le ferait déborder, ce qui recréerait exactement le rectangle
 * disgracieux que DA-033 reproche. La différence est un padding et une taille
 * de glyphe — jamais un dessin de plus.
 */
export type EmptyStateDensity = 'panel' | 'compact'

export interface EmptyStateProps {
  /** Glyphe Lucide, neutre. Un état vide sans glyphe se lit comme un bug d'affichage. */
  icon?: LucideIcon
  /** La phrase : ce qui manque. Passe par i18next, jamais « Aucune donnée ». */
  title: ReactNode
  /** Le détail : pourquoi c'est vide, ou ce que l'action va produire. */
  description?: ReactNode
  /**
   * L'action qui DÉBLOQUE — un `Button`, et un seul.
   *
   * Elle est facultative parce qu'il existe des vides que l'utilisateur ne peut
   * pas remplir lui-même (le panneau de détail de `/admin/permissions` attend
   * une sélection dans la colonne d'à côté). Partout ailleurs, l'absence
   * d'action est une omission, pas un choix.
   */
  action?: ReactNode
  density?: EmptyStateDensity
  /**
   * Niveau du titre. `p` (défaut) : l'état vide occupe une ZONE d'une page qui
   * a déjà son `<h1>`. `h1` : l'état vide TIENT LIEU de page entière — le cas
   * de « Offre non activée » sur `/upgrade`, mesuré SANS titre de page par la
   * recette passe 2 (DA-057). Un écran sans `h1` n'a pas de nom : ni pour un
   * lecteur d'écran, ni pour l'échelle du §2. Le cran passe alors à **T1**,
   * comme tout titre de page (même arbitrage que `ErrorState`).
   */
  titleAs?: 'p' | 'h1'
  className?: string
}

/**
 * Cotes par densité. Écrites en clair : Tailwind scanne des chaînes statiques,
 * une classe assemblée à l'exécution n'est jamais générée (piège documenté dans
 * `StatCard.tsx`).
 */
const DENSITY: Record<EmptyStateDensity, { padding: string; tile: string; glyph: string }> = {
  panel: { padding: 'px-6 py-12', tile: 'h-12 w-12', glyph: 'h-6 w-6' },
  compact: { padding: 'px-4 py-6', tile: 'h-10 w-10', glyph: 'h-5 w-5' },
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  density = 'panel',
  titleAs: TitleTag = 'p',
  className = '',
}: EmptyStateProps) {
  const cotes = DENSITY[density]

  return (
    <div
      className={`flex flex-col items-center justify-center text-center ${cotes.padding} ${className}`.trim()}
    >
      {Icon && (
        // Tuile NEUTRE, au rayon de contrôle — le même geste que la tuile du
        // `PageHeader` (H2) et que les tuiles de section des modales (H7, DA-048).
        <span
          aria-hidden="true"
          className={`mb-3 flex ${cotes.tile} shrink-0 items-center justify-center rounded-control bg-background-secondary text-foreground-tertiary`}
        >
          <Icon className={cotes.glyph} />
        </span>
      )}

      <TitleTag className={`${TitleTag === 'h1' ? 'text-t1' : 'text-t3'} text-foreground`}>
        {title}
      </TitleTag>

      {/* `max-w-[46ch]` : une phrase d'aide centrée sur toute la largeur d'un
          panneau est illisible — c'est le défaut que DA-045 reproche au bloc
          d'aide de `/import`, mesuré à ~1050 px de long. La borne vaut ici
          aussi, et elle est portée par la primitive pour que personne n'ait à
          y penser. */}
      {description && (
        <p className="mt-1 max-w-[46ch] text-t5 text-foreground-secondary">{description}</p>
      )}

      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

export default EmptyState
